import React, { useEffect, useState } from 'react';
import driverApi from '../../clients/DriverClient';
import { type Membership } from '../../models/Membership';
import { EntitlementGrantStatus } from '../../models/entitlement/EntitlementSummary';
import PlanCard from './PlanCard';
import CreditsCard from './CreditsCard';
import RaceNightsSection from './RaceNightsSection';
import { VENUES } from '../raceNights/RaceNights';
import { normalizeLocationKey } from '../../clients/locationApiBase';
import { resolvePreferredLocation } from '../../utils/preferredLocation';

interface DashboardActionsProps {
    driverId?: string;
    firstName?: string;
    /** The member's preferred venue, for race nights. Passed in: useAuth throws outside a provider. */
    preferredLocation?: string | null;
    /** The credits balance (useAuth().balance); null while it loads. */
    balance?: number | null;
}

const preferredVenue = (preferred: string | null | undefined) =>
    resolvePreferredLocation(preferred, (k) => VENUES.some((v) => v.key === k), normalizeLocationKey);

/**
 * The home's header (board HomeV2): the welcome and race nights on the left, the plan card (member) or
 * the Credits card (non-member) on the right, each with the one Book a session button.
 */
const DashboardActions: React.FC<DashboardActionsProps> = ({ driverId, firstName, preferredLocation, balance = null }) => {
    const [membership, setMembership] = useState<Membership | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(!!driverId);
    const [error, setError] = useState<string>('');
    // The preferred venue, or '' ("Pick a venue"). savePreferredLocationIfEmpty makes a member's first
    // booked venue their preference, which is the "most recent booking" fallback.
    const [location, setLocation] = useState(() => preferredVenue(preferredLocation));
    const [picked, setPicked] = useState(false);

    // The profile can arrive after the first render: adopt its venue until the driver picks one here.
    useEffect(() => {
        if (!picked) setLocation(preferredVenue(preferredLocation));
    }, [preferredLocation, picked]);

    useEffect(() => {
        const fetchMembership = async () => {
            if (!driverId) {
                setMembership({ id: '', name: 'None', bookingLinks: [] });
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            setError('');
            try {
                const result = await driverApi.getMembershipByDriverId(driverId);
                setMembership(result);
            } catch (e) {
                setError('Failed to load membership');
            } finally {
                setIsLoading(false);
            }
        };
        fetchMembership();
    }, [driverId]);

    const hasActiveEntitlement =
        membership?.entitlement?.status === EntitlementGrantStatus.Active;
    const hasMembership = !!membership && membership.name !== 'None';
    const isMember = hasActiveEntitlement || hasMembership;
    // Unknown while loading: the section keeps its placeholders, so it and the card appear together.
    // After a failed load the section still shows Public nights; the card area shows the error.
    const sectionIsMember = isLoading ? undefined : !error && isMember;

    // The driver-info-main block (heading + subtitle) is always rendered so the
    // page always has an <h1>, regardless of fetch state. The race-nights section
    // is always mounted too, so its list fetch starts alongside the membership fetch.
    return (
        <div className="driver-info-header">
            <div className="driver-info-main">
                <h1 className="mb-3 heading">Welcome back, {firstName}!</h1>
                <p className="text-paragraphs mb-0">
                    This is your driver dashboard where you can manage your account and view important information.
                </p>
            </div>

            {/* Direct siblings so CSS grid areas can reorder them: on mobile the card (aside) sits
                between the welcome (main) and race nights; on desktop main + race nights stack on the
                left and the card sits on the right. */}
            <div className="driver-info-aside">
                {isLoading && (
                    <div className="text-center py-4">
                        <span className="spinner-border text-primary" role="status" aria-label="Loading your plan"></span>
                    </div>
                )}
                {!isLoading && error && <div className="alert alert-danger mb-0" role="alert">{error}</div>}
                {!isLoading && !error && membership && (isMember
                    ? <PlanCard membership={membership} balance={balance} />
                    : <CreditsCard balance={balance} />)}
            </div>

            <div className="driver-info-nights">
                <RaceNightsSection
                    location={location}
                    onLocationChange={(loc) => { setPicked(true); setLocation(loc); }}
                    isMember={sectionIsMember}
                />
            </div>
        </div>
    );
};

export default DashboardActions;
